import React, { useState } from 'react';
import { cn } from '@hero/web-lib';
import { Spinner } from '../Spinner';
import { GrowthIndicator } from './GrowthIndicator';
import { AnalyticsMiniTabs } from './AnalyticsMiniTabs';

export interface AnalyticsCardOption {
  key: string;
  label: string;
  count: number;
  /** Growth percentage shown next to the count when this option is active */
  growthValue?: number | null;
  /** Label beside the growth (e.g. "vs last week") */
  growthLabel?: string;
  /** Short description shown next to the count (alternative to growth) */
  description?: string;
}

export interface AnalyticsCardProps {
  title: string;
  /** Tab options — first option is selected by default. Card manages its own active tab. */
  options: AnalyticsCardOption[];
  icon?: React.ReactNode;
  /** Tailwind bg class for the icon container (e.g. "bg-blue-50") */
  iconBg?: string;
  /** Persistent growth percentage — always shown regardless of active tab. Overrides option-level growth. */
  growthValue?: number | null;
  /** Label beside the persistent growth (e.g. "vs last week") */
  growthLabel?: string;
  /**
   * Rendered below the card body. Accepts a render function so the
   * footer can react to the currently active tab (e.g. the Delivered
   * card's footer flips earnings between today / this week / this
   * month alongside the count).
   */
  footer?: React.ReactNode | ((activeKey: string) => React.ReactNode);
  isLoading: boolean;
}

export const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  options,
  icon,
  iconBg = 'bg-gray-50',
  growthValue,
  growthLabel,
  footer,
  isLoading,
}) => {
  const [activeKey, setActiveKey] = useState(options[0]?.key);
  const active = options.find(o => o.key === activeKey) ?? options[0];

  // Component-level growth persists across tabs; option-level only shows for that tab
  const showGrowth = growthValue !== undefined ? growthValue : active.growthValue;
  const showGrowthLabel = growthValue !== undefined ? growthLabel : active.growthLabel;

  return (
    <div className="p-5 bg-white border rounded-[10px] flex flex-col justify-between min-h-[140px]">
      <div className="flex justify-between items-start">
        <div>
          <h4 className="text-[14px] font-[400] leading-[22px] text-[#7D7D91]">{title}</h4>
          <div className="flex items-end gap-2 mt-2">
            <h3 className="text-[26px] font-[500] leading-[41px] text-black">
              {isLoading ? <Spinner /> : active.count.toLocaleString()}
            </h3>
            {!isLoading && showGrowth !== undefined && (
              <GrowthIndicator growth={showGrowth} growthLabel={showGrowthLabel} />
            )}
            {!isLoading && !showGrowth && active.description && (
              <span className="text-xs text-gray-400">{active.description}</span>
            )}
          </div>
        </div>
        {icon && (
          <div className={cn('p-2 rounded-lg shrink-0', iconBg)}>{icon}</div>
        )}
      </div>
      {!isLoading && (
        <AnalyticsMiniTabs
          options={options}
          activeKey={activeKey}
          onChange={setActiveKey}
        />
      )}
      {!isLoading && (typeof footer === 'function' ? footer(active.key) : footer)}
    </div>
  );
};
